import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import Loader from "./components/shared/loader";
import { NotFoundPage } from "./components/shared/not-found";
import "./index.css";
import { initSentry } from "./lib/sentry";
import { routeTree } from "./routeTree.gen";
import { getQueryClient, orpc } from "./utils/orpc";

/**
 * Router factory with per-request isolation for SSR.
 * @param request - Optional incoming request for SSR (unused in boiler-2 pattern)
 */
export const getRouter = (_request?: Request) => {
	// Get per-request QueryClient (new on server, singleton in browser)
	const queryClient = getQueryClient();

	const router = createTanStackRouter({
		routeTree,
		scrollRestoration: true,
		defaultPreloadStaleTime: 5 * 60 * 1000, // 5 minutes - matches global staleTime to prevent refetch on hydration
		context: { orpc, queryClient },
		defaultPendingComponent: () => <Loader />,
		defaultNotFoundComponent: NotFoundPage,
		Wrap: ({ children }) => (
			<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
		),
	});

	// SSR Query Integration - enables automatic query dehydration/hydration
	// Queries fetched during SSR are automatically serialized and rehydrated on client
	setupRouterSsrQueryIntegration({ router, queryClient });

	// Initialize Sentry with router integration for automatic route tracking
	// initSentry checks for client-side environment and DSN availability
	initSentry(router);

	return router;
};
